import { PlayerInfoFragment, PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { League, PlayerAttributesType } from '@/types/api.generated';

import { NFL_FULL_SEASON } from './constants';
import { findPlayerAttribute } from './fantasy-attribute-utils';
import { isCollegeSport, isPGA, isTennis, isUFC } from './league';

export const formatPlayerInfo = ({
    eventTime,
    readOnly,
    projections,
    teamInfoText,
    nflFullSeason,
}: {
    eventTime?: string;
    readOnly?: boolean;
    projections?: readonly PlayerProjectionFragment[];
    teamInfoText?: string;
    nflFullSeason?: boolean;
}) => {
    const noOfProjections = projections?.length || 0;
    const projectionsText = projections ? (readOnly ? '#' + noOfProjections : `${noOfProjections} proj`) : null;

    if (nflFullSeason) {
        return [NFL_FULL_SEASON, projectionsText].filter(Boolean).join(' • ');
    }

    return [teamInfoText, eventTime, projectionsText].filter(Boolean).join(' • ');
};

const formatRankAndCountry = (player: PlayerInfoFragment, rankAttribute: PlayerAttributesType) => {
    const rank = findPlayerAttribute(player, rankAttribute);
    const country = findPlayerAttribute(player, PlayerAttributesType.CountryCode);

    if (rank) {
        if (!country) {
            return ` • #${rank}`;
        }

        return ` • #${rank}, ${country}`;
    }

    return country ? ` • ${country}` : '';
};

export const formatPlayerPositionAndTeam = (player: PlayerInfoFragment, teamName?: string, league?: League) => {
    if (!league) {
        return '';
    }

    if (isTennis(league)) {
        return formatRankAndCountry(player, PlayerAttributesType.TennisRank);
    }

    if (isPGA(league)) {
        return formatRankAndCountry(player, PlayerAttributesType.GolfRank);
    }

    if (isUFC(league)) {
        return '';
    }

    return ` • ${player.position}, ${teamName}`;
};

export const getPlayerPositionOrRank = (player: PlayerInfoFragment, event?: EventInfo) => {
    if (!event) {
        return '';
    }

    if (isTennis(event.league)) {
        const tennisRank = findPlayerAttribute(player, PlayerAttributesType.TennisRank);
        return tennisRank ? `#${tennisRank}` : '';
    }

    if (isPGA(event?.league)) {
        const golfRank = findPlayerAttribute(player, PlayerAttributesType.GolfRank);
        return golfRank ? `#${golfRank}` : '';
    }

    if (isUFC(event?.league)) {
        return '';
    }

    return player.position || '';
};

//if jersey number is null/undefined, return 99 for college sports
export const getPlayerJerseyNumber = (league?: League, jerseyNumber?: number) => {
    return isCollegeSport(league) ? jerseyNumber ?? 99 : null;
};

/**
 * Should show college shirt with jersey number only if player has jersey number and player image is a placeholder.
 * For other cases, we should show player image
 */
export const shouldShowShirtImage = (
    playerNumber: number | null | undefined,
    hasPlaceholderImage: boolean
): playerNumber is number => {
    return typeof playerNumber === 'number' && hasPlaceholderImage;
};
